import React, { useState } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import api from '../../services/api';
import toast from 'react-hot-toast';
import '../../styles/debt-payment.css';

const DebtPayment = ({ debt, onClose, onComplete }) => {
  const [formData, setFormData] = useState({
    amount: debt.remaining_amount,
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    if (formData.amount > debt.remaining_amount) {
      toast.error('Amount cannot exceed remaining balance');
      return;
    }

    setLoading(true);
    try {
      await api.post('/debts.php/payment', {
        debt_id: debt.id,
        ...formData
      });

      toast.success('Payment recorded successfully');
      onComplete();
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.error || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [
    debt.remaining_amount,
    debt.remaining_amount / 2,
    debt.remaining_amount / 4,
    1000, 2000, 5000
  ].filter(amount => amount > 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Record Payment" size="md">
      <form onSubmit={handleSubmit} className="debt-payment-form">
        <div className="debt-summary">
          <div className="summary-row">
            <span>Customer:</span>
            <strong>{debt.first_name} {debt.last_name}</strong>
          </div>
          <div className="summary-row">
            <span>Remaining Balance:</span>
            <strong className="balance">{formatCurrency(debt.remaining_amount)}</strong>
          </div>
        </div>

        <div className="form-group">
          <label>Payment Amount *</label>
          <Input
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
            min="0.01"
            max={debt.remaining_amount}
            step="any"
            required
            autoFocus
          />
        </div>

        <div className="quick-amounts">
          {quickAmounts.map(amount => (
            <button
              key={amount}
              type="button"
              className="quick-amount-btn"
              onClick={() => setFormData({ ...formData, amount })}
            >
              {formatCurrency(amount)}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label>Payment Method</label>
          <select
            value={formData.payment_method}
            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
            className="payment-select"
          >
            <option value="cash">Cash</option>
            <option value="mpesa">M-Pesa</option>
            <option value="bank">Bank Transfer</option>
            <option value="card">Card</option>
          </select>
        </div>

        {formData.payment_method === 'mpesa' && (
          <div className="form-group">
            <label>M-Pesa Transaction Code</label>
            <Input
              value={formData.reference_number}
              onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              placeholder="e.g., QW1234XYZ"
            />
          </div>
        )}

        {formData.payment_method === 'bank' && (
          <div className="form-group">
            <label>Reference Number</label>
            <Input
              value={formData.reference_number}
              onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              placeholder="Bank reference/transaction ID"
            />
          </div>
        )}

        <div className="form-group">
          <label>Notes (Optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows="3"
            placeholder="Additional notes about this payment"
          />
        </div>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Record Payment
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default DebtPayment;